import {
  Circle,
  FabricImage,
  FabricText,
  Group,
  Line,
  Path,
  Polygon,
  Polyline,
  Rect,
  util,
} from 'fabric';
import type { FabricObject } from 'fabric';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type { ParamField, ToolRecipe } from '../types';

/**
 * Chart recipes: bar, pie, line, pictograph.
 *
 * Every bar/wedge/point/label is its own primitive inside the group so a
 * teacher can ungroup and recolor or nudge individual pieces. Data arrives as
 * a list param — an array of { label, value, color? } items (json-era string
 * params are still parsed); anything malformed falls back to sample data so
 * generate() never throws on user input.
 */

interface ChartDatum {
  label: string;
  value: number;
  color?: string;
}

/** Vibrant, school-friendly fallback palette for items that omit `color`. */
const PALETTE = [
  '#FF6B6B',
  '#4ECDC4',
  '#FFD93D',
  '#6C5CE7',
  '#51CF66',
  '#FF9F43',
  '#54A0FF',
  '#F368E0',
];

const GRID_COLOR = '#E3E7EE';
const AXIS_COLOR = '#B9C0CC';
const TEXT_COLOR = '#333333';
/** Single stroke color for the line chart's polyline/area (markers stay per-datum). */
const LINE_COLOR = '#54A0FF';

const SAMPLE_DATA: ChartDatum[] = [
  { label: 'Apples', value: 8 },
  { label: 'Bananas', value: 5 },
  { label: 'Grapes', value: 12 },
  { label: 'Oranges', value: 6 },
];

function asNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function asString(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function asBoolean(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * The list editor and regeneration hand us the array itself; templates saved
 * in the json era may hand us a string. Accept both, drop malformed entries, and clamp negatives (none
 * of these chart types can draw below the baseline). Empty result → sample
 * data, so downstream math never divides by an empty set.
 */
function parseData(raw: unknown): ChartDatum[] {
  let candidate: unknown = raw;
  if (typeof raw === 'string') {
    try {
      candidate = JSON.parse(raw);
    } catch {
      candidate = undefined;
    }
  }
  const items: ChartDatum[] = [];
  if (Array.isArray(candidate)) {
    for (const entry of candidate) {
      if (!entry || typeof entry !== 'object') continue;
      const { label, value, color } = entry as Record<string, unknown>;
      if (typeof label !== 'string' || typeof value !== 'number' || !Number.isFinite(value)) {
        continue;
      }
      const datum: ChartDatum = { label, value: Math.max(0, value) };
      if (typeof color === 'string' && color) datum.color = color;
      items.push(datum);
    }
  }
  return items.length > 0 ? items : SAMPLE_DATA.map((d) => ({ ...d }));
}

function colorFor(item: ChartDatum, index: number): string {
  return item.color ?? PALETTE[index % PALETTE.length];
}

/** Round the data max up to a 1/2/2.5/5×10^n value so gridlines land on friendly numbers. */
function niceMax(value: number): number {
  if (value <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(value));
  const frac = value / mag;
  const nice = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return nice * mag;
}

/** Keep serialized path/point coordinates short. */
function r2(n: number): number {
  return Math.round(n * 100) / 100;
}

function fmt(v: number): string {
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10);
}

interface CommonOpts {
  width: number;
  height: number;
  items: ChartDatum[];
  fontFamily: string;
  labelFontSize: number;
  showValues: boolean;
  title: string;
}

function readCommon(params: Record<string, unknown>): CommonOpts {
  return {
    width: Math.max(80, asNumber(params.width, 480)),
    height: Math.max(80, asNumber(params.height, 320)),
    items: parseData(params.data),
    fontFamily: asString(params.fontFamily, 'Arial'),
    labelFontSize: Math.max(6, asNumber(params.labelFontSize, 14)),
    showValues: asBoolean(params.showValues, true),
    title: asString(params.title, ''),
  };
}

/** Title (when non-empty) plus the y where chart content should begin. */
function buildTitle(opts: CommonOpts): { objects: FabricObject[]; contentTop: number } {
  if (!opts.title.trim()) return { objects: [], contentTop: 0 };
  const fontSize = opts.labelFontSize * 1.5;
  const text = new FabricText(opts.title, {
    left: opts.width / 2,
    top: 0,
    originX: 'center',
    fontFamily: opts.fontFamily,
    fontSize,
    fontWeight: 'bold',
    fill: TEXT_COLOR,
  });
  return { objects: [text], contentTop: fontSize * 1.7 };
}

/** Absolute-coordinate objects → group repositioned to the 0,0 contract. */
function finish(
  objects: FabricObject[],
  recipe: Pick<ToolRecipe, 'id' | 'version'>,
  params: Record<string, unknown>,
): FabricObject {
  const group = new Group(objects);
  group.set({ left: 0, top: 0 });
  return tagSmartObject(group, recipe, params);
}

function commonSchema(): ParamField[] {
  return [
    { key: 'title', label: 'Title', type: 'text', help: 'Leave empty for no title.' },
    {
      key: 'data',
      label: 'Data',
      type: 'list',
      itemSchema: [
        { key: 'label', label: 'Label', type: 'text' },
        { key: 'value', label: 'Value', type: 'number', min: 0 },
        {
          key: 'color',
          label: 'Color',
          type: 'color',
          help: 'Leave unset to use the automatic palette.',
        },
      ],
      summaryKeys: ['label', 'value'],
      minItems: 1,
      maxItems: 12,
      help: 'One item per bar / slice / point.',
    },
    {
      key: 'width',
      label: 'Width',
      type: 'number',
      min: 80,
      max: 4000,
      step: 10,
      advanced: true,
    },
    {
      key: 'height',
      label: 'Height',
      type: 'number',
      min: 80,
      max: 4000,
      step: 10,
      advanced: true,
    },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'labelFontSize',
      label: 'Label size',
      type: 'number',
      min: 6,
      max: 72,
      step: 1,
      advanced: true,
    },
    { key: 'showValues', label: 'Show values', type: 'boolean' },
  ];
}

const COMMON_DEFAULTS: Record<string, unknown> = {
  title: '',
  data: SAMPLE_DATA,
  width: 480,
  height: 320,
  fontFamily: 'Arial',
  labelFontSize: 14,
  showValues: true,
};

/* ------------------------------------------------------------------ bar */

const barChart: ToolRecipe = {
  id: 'bar-chart',
  domain: 'math',
  tags: ['chart', 'data', 'graph', 'bar-graph', 'statistics'],
  featured: true,
  name: 'Bar Chart',
  category: 'chart',
  version: 1,
  description: 'Vertical bar chart with gridlines, value labels, and category labels.',
  defaults: { ...COMMON_DEFAULTS },
  schema: commonSchema(),
  // Smart Tools modal exemplar (SM-A, 2026-07-23): the data IS the chart —
  // pin title + data as hero controls; the short remaining schema needs no
  // section cards or columns.
  modal: { hero: ['title', 'data'] },
  // Honest band values: label size tracks reading level; faces step from the
  // rounded K-2 Fredoka through Nunito/Lexend to a neutral academic Arial.
  // Younger bands keep value labels on (counting support).
  gradeBands: {
    K2: { fontFamily: 'Fredoka', labelFontSize: 22, showValues: true },
    G35: { fontFamily: 'Nunito', labelFontSize: 17, showValues: true },
    G68: { fontFamily: 'Lexend', labelFontSize: 14 },
    G912: { fontFamily: 'Arial', labelFontSize: 12 },
  },
  async generate(params) {
    const opts = readCommon(params);
    const { objects, contentTop } = buildTitle(opts);
    const { width, height, items, fontFamily, labelFontSize } = opts;

    // Headroom above bars for value labels, room below for category labels.
    const plotTop = contentTop + (opts.showValues ? labelFontSize * 1.6 : labelFontSize * 0.5);
    const plotBottom = height - labelFontSize * 1.8;
    const plotH = Math.max(10, plotBottom - plotTop);
    const yMax = niceMax(Math.max(...items.map((d) => d.value)));

    // Gridlines first so they sit behind the bars; baseline gets a darker stroke.
    const divisions = 4;
    for (let i = 0; i <= divisions; i++) {
      const y = r2(plotTop + (plotH * i) / divisions);
      objects.push(
        new Line([0, y, width, y], {
          stroke: i === divisions ? AXIS_COLOR : GRID_COLOR,
          strokeWidth: 1,
        }),
      );
    }

    const slot = width / items.length;
    const barW = slot * 0.6;
    items.forEach((item, i) => {
      const barH = (item.value / yMax) * plotH;
      const x = r2(i * slot + (slot - barW) / 2);
      const barTop = r2(plotBottom - barH);
      objects.push(
        new Rect({
          left: x,
          top: barTop,
          width: r2(barW),
          height: r2(barH),
          fill: colorFor(item, i),
          rx: 2,
          ry: 2,
        }),
      );
      if (opts.showValues) {
        objects.push(
          new FabricText(fmt(item.value), {
            left: r2(i * slot + slot / 2),
            top: r2(barTop - labelFontSize * 1.25),
            originX: 'center',
            fontFamily,
            fontSize: labelFontSize,
            fill: TEXT_COLOR,
          }),
        );
      }
      objects.push(
        new FabricText(item.label, {
          left: r2(i * slot + slot / 2),
          top: r2(plotBottom + labelFontSize * 0.35),
          originX: 'center',
          fontFamily,
          fontSize: labelFontSize,
          fill: TEXT_COLOR,
        }),
      );
    });

    return finish(objects, barChart, params);
  },
};

/* ------------------------------------------------------------------ pie */

const pieChart: ToolRecipe = {
  id: 'pie-chart',
  domain: 'math',
  tags: ['chart', 'data', 'graph', 'pie-chart', 'percentages', 'fractions'],
  name: 'Pie Chart',
  category: 'chart',
  version: 1,
  description: 'Pie chart with percentage labels and an optional legend.',
  defaults: { ...COMMON_DEFAULTS, showLegend: true },
  schema: [...commonSchema(), { key: 'showLegend', label: 'Show legend', type: 'boolean' }],
  async generate(params) {
    const opts = readCommon(params);
    const showLegend = asBoolean(params.showLegend, true);
    const { objects, contentTop } = buildTitle(opts);
    const { width, height, fontFamily, labelFontSize } = opts;

    // All-zero data would make wedge fractions undefined — swap in the sample.
    let items = opts.items;
    if (items.reduce((s, d) => s + d.value, 0) <= 0) {
      items = SAMPLE_DATA.map((d) => ({ ...d }));
    }
    const total = items.reduce((s, d) => s + d.value, 0);

    // Legend column width is measured from the rendered labels so long names
    // don't collide with the pie; capped at 45% of the chart width.
    const legendTexts = showLegend
      ? items.map(
          (d) =>
            new FabricText(d.label, { fontFamily, fontSize: labelFontSize, fill: TEXT_COLOR }),
        )
      : [];
    const swatch = labelFontSize;
    const legendW = showLegend
      ? Math.min(
          width * 0.45,
          swatch + 8 + Math.max(...legendTexts.map((t) => t.width ?? 0)) + 8,
        )
      : 0;

    const availH = height - contentTop;
    const cx = (width - legendW) / 2;
    const cy = contentTop + availH / 2;
    const r = Math.max(10, Math.min(width - legendW, availH) / 2 - 6);

    // Wedges start at 12 o'clock and sweep clockwise (sweep flag 1, y-down).
    let angle = -Math.PI / 2;
    items.forEach((item, i) => {
      const frac = item.value / total;
      if (frac <= 0) return;
      const fill = colorFor(item, i);
      if (frac >= 0.99999) {
        // A single arc command cannot draw a closed 360° sweep — use two halves.
        const d = `M ${r2(cx)} ${r2(cy - r)} A ${r2(r)} ${r2(r)} 0 1 1 ${r2(cx)} ${r2(cy + r)} A ${r2(r)} ${r2(r)} 0 1 1 ${r2(cx)} ${r2(cy - r)} Z`;
        objects.push(new Path(d, { fill }));
      } else {
        const end = angle + frac * Math.PI * 2;
        const x1 = cx + r * Math.cos(angle);
        const y1 = cy + r * Math.sin(angle);
        const x2 = cx + r * Math.cos(end);
        const y2 = cy + r * Math.sin(end);
        const largeArc = frac > 0.5 ? 1 : 0;
        const d = `M ${r2(cx)} ${r2(cy)} L ${r2(x1)} ${r2(y1)} A ${r2(r)} ${r2(r)} 0 ${largeArc} 1 ${r2(x2)} ${r2(y2)} Z`;
        objects.push(new Path(d, { fill }));
      }
      if (opts.showValues) {
        const mid = frac >= 0.99999 ? -Math.PI / 2 : angle + (frac * Math.PI * 2) / 2;
        objects.push(
          new FabricText(`${Math.round(frac * 100)}%`, {
            left: r2(cx + r * 0.62 * Math.cos(mid)),
            top: r2(cy + r * 0.62 * Math.sin(mid)),
            originX: 'center',
            originY: 'center',
            fontFamily,
            fontSize: labelFontSize,
            fontWeight: 'bold',
            fill: '#FFFFFF',
          }),
        );
      }
      angle += frac * Math.PI * 2;
    });

    if (showLegend) {
      const rowH = labelFontSize * 1.6;
      const legendX = width - legendW;
      let y = cy - (items.length * rowH) / 2;
      items.forEach((item, i) => {
        objects.push(
          new Rect({
            left: r2(legendX),
            top: r2(y + (rowH - swatch) / 2),
            width: swatch,
            height: swatch,
            fill: colorFor(item, i),
            rx: 2,
            ry: 2,
          }),
        );
        const text = legendTexts[i];
        text.set({
          left: r2(legendX + swatch + 8),
          top: r2(y + rowH / 2),
          originY: 'center',
        });
        objects.push(text);
        y += rowH;
      });
    }

    return finish(objects, pieChart, params);
  },
};

/* ----------------------------------------------------------------- line */

const lineChart: ToolRecipe = {
  id: 'line-chart',
  domain: 'math',
  tags: ['chart', 'data', 'graph', 'line-graph', 'trends', 'statistics'],
  name: 'Line Chart',
  category: 'chart',
  version: 1,
  description: 'Line chart with markers, gridlines, and an optional area fill.',
  defaults: { ...COMMON_DEFAULTS, showArea: true },
  schema: [...commonSchema(), { key: 'showArea', label: 'Fill area', type: 'boolean' }],
  async generate(params) {
    const opts = readCommon(params);
    const showArea = asBoolean(params.showArea, true);
    const { objects, contentTop } = buildTitle(opts);
    const { width, height, items, fontFamily, labelFontSize } = opts;

    const plotTop = contentTop + (opts.showValues ? labelFontSize * 1.6 : labelFontSize * 0.5);
    const plotBottom = height - labelFontSize * 1.8;
    const plotH = Math.max(10, plotBottom - plotTop);
    const yMax = niceMax(Math.max(...items.map((d) => d.value)));

    const divisions = 4;
    for (let i = 0; i <= divisions; i++) {
      const y = r2(plotTop + (plotH * i) / divisions);
      objects.push(
        new Line([0, y, width, y], {
          stroke: i === divisions ? AXIS_COLOR : GRID_COLOR,
          strokeWidth: 1,
        }),
      );
    }

    // Points are centered in category slots so single-item data still renders.
    const slot = width / items.length;
    const pts = items.map((item, i) => ({
      x: r2(i * slot + slot / 2),
      y: r2(plotBottom - (item.value / yMax) * plotH),
    }));

    if (showArea && pts.length > 1) {
      objects.push(
        new Polygon(
          [
            ...pts,
            { x: pts[pts.length - 1].x, y: r2(plotBottom) },
            { x: pts[0].x, y: r2(plotBottom) },
          ],
          { fill: LINE_COLOR, opacity: 0.25 },
        ),
      );
    }
    if (pts.length > 1) {
      objects.push(
        new Polyline(pts, {
          fill: '',
          stroke: LINE_COLOR,
          strokeWidth: 3,
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
        }),
      );
    }

    const markerR = Math.max(3, labelFontSize * 0.3);
    items.forEach((item, i) => {
      objects.push(
        new Circle({
          left: r2(pts[i].x - markerR),
          top: r2(pts[i].y - markerR),
          radius: markerR,
          fill: colorFor(item, i),
          stroke: '#FFFFFF',
          strokeWidth: 1.5,
        }),
      );
      if (opts.showValues) {
        objects.push(
          new FabricText(fmt(item.value), {
            left: pts[i].x,
            top: r2(pts[i].y - markerR - labelFontSize * 1.25),
            originX: 'center',
            fontFamily,
            fontSize: labelFontSize,
            fill: TEXT_COLOR,
          }),
        );
      }
      objects.push(
        new FabricText(item.label, {
          left: pts[i].x,
          top: r2(plotBottom + labelFontSize * 0.35),
          originX: 'center',
          fontFamily,
          fontSize: labelFontSize,
          fill: TEXT_COLOR,
        }),
      );
    });

    return finish(objects, lineChart, params);
  },
};

/* ----------------------------------------------------------- pictograph */

const pictograph: ToolRecipe = {
  id: 'pictograph',
  domain: 'math',
  tags: ['chart', 'data', 'graph', 'pictograph', 'counting', 'K-2'],
  name: 'Pictograph',
  category: 'chart',
  version: 1,
  description: 'Rows of repeated icons — one icon per unit value, with a key at the bottom.',
  defaults: { ...COMMON_DEFAULTS, iconUrl: '', unitValue: 1 },
  schema: [
    ...commonSchema(),
    {
      key: 'iconUrl',
      label: 'Icon',
      type: 'image',
      help: 'The picture that repeats — each copy stands for “units per icon”. Leave empty for colored dots.',
    },
    { key: 'unitValue', label: 'Units per icon', type: 'number', min: 0.1, max: 1000000, step: 1 },
  ],
  async generate(params) {
    const opts = readCommon(params);
    const iconUrl = asString(params.iconUrl, '');
    const unitValue = Math.max(0.000001, asNumber(params.unitValue, 1));
    const { objects, contentTop } = buildTitle(opts);
    const { width, height, items, fontFamily, labelFontSize } = opts;

    // Bad URLs must degrade, not throw — a broken template param would
    // otherwise take the whole poster render down with it.
    let iconEl: Awaited<ReturnType<typeof util.loadImage>> | null = null;
    if (iconUrl.trim()) {
      try {
        iconEl = await util.loadImage(iconUrl, { crossOrigin: 'anonymous' });
      } catch {
        iconEl = null;
      }
    }

    // Cap icon counts so a tiny unitValue can't explode into thousands of objects.
    const MAX_ICONS = 40;
    const counts = items.map((d) => Math.min(MAX_ICONS, Math.max(0, Math.round(d.value / unitValue))));
    const maxCount = Math.max(1, ...counts);

    const keyH = labelFontSize * 2.2;
    const rowsTop = contentTop;
    const rowH = Math.max(labelFontSize * 1.4, (height - rowsTop - keyH) / items.length);

    // Label column sized from the rendered row labels so icons never overlap text.
    const labelTexts = items.map(
      (d) => new FabricText(d.label, { fontFamily, fontSize: labelFontSize, fill: TEXT_COLOR }),
    );
    const labelColW = Math.max(...labelTexts.map((t) => t.width ?? 0)) + labelFontSize;

    const valueColW = opts.showValues ? labelFontSize * 2.5 : 0;
    const iconAreaW = Math.max(20, width - labelColW - valueColW);
    const iconSize = Math.max(6, Math.min(rowH * 0.75, (iconAreaW / maxCount) * 0.9));
    const step = Math.min(iconAreaW / maxCount, iconSize * 1.25);

    // One FabricImage per icon, all sharing the single loaded element.
    const scale = iconEl ? iconSize / Math.max(iconEl.width, iconEl.height, 1) : 1;
    const makeIcon = (cx: number, cy: number, fill: string): FabricObject =>
      iconEl
        ? new FabricImage(iconEl, {
            left: r2(cx),
            top: r2(cy),
            originX: 'center',
            originY: 'center',
            scaleX: scale,
            scaleY: scale,
          })
        : new Circle({
            left: r2(cx - iconSize / 2),
            top: r2(cy - iconSize / 2),
            radius: r2(iconSize / 2),
            fill,
          });

    items.forEach((item, i) => {
      const cy = rowsTop + i * rowH + rowH / 2;
      labelTexts[i].set({ left: 0, top: r2(cy), originY: 'center' });
      objects.push(labelTexts[i]);
      const fill = colorFor(item, i);
      for (let k = 0; k < counts[i]; k++) {
        objects.push(makeIcon(labelColW + k * step + iconSize / 2, cy, fill));
      }
      if (opts.showValues) {
        objects.push(
          new FabricText(fmt(item.value), {
            left: r2(labelColW + counts[i] * step + labelFontSize * 0.5),
            top: r2(cy),
            originY: 'center',
            fontFamily,
            fontSize: labelFontSize,
            fill: TEXT_COLOR,
          }),
        );
      }
    });

    // Key: sample icon + what one icon stands for.
    const keyCy = height - keyH / 2;
    objects.push(makeIcon(iconSize / 2, keyCy, PALETTE[0]));
    objects.push(
      new FabricText(`= ${fmt(unitValue)} per icon`, {
        left: r2(iconSize + labelFontSize * 0.5),
        top: r2(keyCy),
        originY: 'center',
        fontFamily,
        fontSize: labelFontSize,
        fill: TEXT_COLOR,
      }),
    );

    return finish(objects, pictograph, params);
  },
};

registerRecipe(barChart);
registerRecipe(pieChart);
registerRecipe(lineChart);
registerRecipe(pictograph);
